'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertTriangle, Info, ChevronRight, ChevronLeft, Users, Calendar, Heart, Bone, ArrowLeft } from 'lucide-react';
import BookingForm from '../booking-form';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

interface Question {
    id: number;
    question: string;
    options: { value: string; label: string; score: number }[];
}

interface Result {
    score: number;
    title: string;
    description: string;
    recommendation: string;
    additionalInfo?: string[];
}

export default function EligibilitySection() {
    const { t } = useTranslation('dental_implants');
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const [showQuiz, setShowQuiz] = useState(false);
    const [showBooking, setShowBooking] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [showResult, setShowResult] = useState(false);
    const [result, setResult] = useState<Result | null>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const formRef = useRef<HTMLDivElement>(null);

    const eligibilityFactors = [
        {
            icon: <Calendar className="w-6 h-6 text-gold" />,
            label: t('eligibility.factors.age.title'),
            description: t('eligibility.factors.age.description')
        },
        {
            icon: <Heart className="w-6 h-6 text-gold" />,
            label: t('eligibility.factors.gums.title'),
            description: t('eligibility.factors.gums.description')
        },
        {
            icon: <Bone className="w-6 h-6 text-gold" />,
            label: t('eligibility.factors.bone.title'),
            description: t('eligibility.factors.bone.description')
        },
        {
            icon: <Users className="w-6 h-6 text-gold" />,
            label: t('eligibility.factors.conditions.title'),
            description: t('eligibility.factors.conditions.description')
        }
    ];

    const questions: Question[] = [
        {
            id: 1,
            question: t('eligibility.quiz.questions.q1.question'),
            options: [
                { value: "1", label: t('eligibility.quiz.questions.q1.options.one'), score: 10 },
                { value: "2-3", label: t('eligibility.quiz.questions.q1.options.two_three'), score: 8 },
                { value: "4-6", label: t('eligibility.quiz.questions.q1.options.four_six'), score: 6 },
                { value: "full-arch", label: t('eligibility.quiz.questions.q1.options.full_arch'), score: 4 }
            ]
        },
        {
            id: 2,
            question: t('eligibility.quiz.questions.q2.question'),
            options: [
                { value: "recent", label: t('eligibility.quiz.questions.q2.options.recent'), score: 10 },
                { value: "6-12", label: t('eligibility.quiz.questions.q2.options.six_twelve'), score: 8 },
                { value: "1-2", label: t('eligibility.quiz.questions.q2.options.one_two'), score: 6 },
                { value: "2+", label: t('eligibility.quiz.questions.q2.options.two_plus'), score: 4 }
            ]
        },
        {
            id: 3,
            question: t('eligibility.quiz.questions.q3.question'),
            options: [
                { value: "diabetes", label: t('eligibility.quiz.questions.q3.options.diabetes'), score: 6 },
                { value: "smoking", label: t('eligibility.quiz.questions.q3.options.smoking'), score: 4 },
                { value: "multiple", label: t('eligibility.quiz.questions.q3.options.multiple'), score: 2 },
                { value: "none", label: t('eligibility.quiz.questions.q3.options.none'), score: 10 }
            ]
        },
        {
            id: 4,
            question: t('eligibility.quiz.questions.q4.question'),
            options: [
                { value: "excellent", label: t('eligibility.quiz.questions.q4.options.excellent'), score: 10 },
                { value: "good", label: t('eligibility.quiz.questions.q4.options.good'), score: 8 },
                { value: "fair", label: t('eligibility.quiz.questions.q4.options.fair'), score: 6 },
                { value: "poor", label: t('eligibility.quiz.questions.q4.options.poor'), score: 2 }
            ]
        },
        {
            id: 5,
            question: t('eligibility.quiz.questions.q5.question'),
            options: [
                { value: "function", label: t('eligibility.quiz.questions.q5.options.function'), score: 8 },
                { value: "appearance", label: t('eligibility.quiz.questions.q5.options.appearance'), score: 8 },
                { value: "pain", label: t('eligibility.quiz.questions.q5.options.pain'), score: 6 },
                { value: "all", label: t('eligibility.quiz.questions.q5.options.all'), score: 10 }
            ]
        }
    ];

    const getResult = (totalScore: number): Result => {
        if (totalScore >= 40) {
            return {
                score: totalScore,
                title: t('eligibility.quiz.results.excellent.title'),
                description: t('eligibility.quiz.results.excellent.description'),
                recommendation: t('eligibility.quiz.results.excellent.recommendation'),
                additionalInfo: [
                    t('eligibility.quiz.results.excellent.info.0'),
                    t('eligibility.quiz.results.excellent.info.1'),
                    t('eligibility.quiz.results.excellent.info.2')
                ]
            };
        } else if (totalScore >= 30) {
            return {
                score: totalScore,
                title: t('eligibility.quiz.results.good.title'),
                description: t('eligibility.quiz.results.good.description'),
                recommendation: t('eligibility.quiz.results.good.recommendation'),
                additionalInfo: [
                    t('eligibility.quiz.results.good.info.0'),
                    t('eligibility.quiz.results.good.info.1'),
                    t('eligibility.quiz.results.good.info.2')
                ]
            };
        } else if (totalScore >= 20) {
            return {
                score: totalScore,
                title: t('eligibility.quiz.results.possible.title'),
                description: t('eligibility.quiz.results.possible.description'),
                recommendation: t('eligibility.quiz.results.possible.recommendation'),
                additionalInfo: [
                    t('eligibility.quiz.results.possible.info.0'),
                    t('eligibility.quiz.results.possible.info.1'),
                    t('eligibility.quiz.results.possible.info.2')
                ]
            };
        } else {
            return {
                score: totalScore,
                title: t('eligibility.quiz.results.consultation.title'),
                description: t('eligibility.quiz.results.consultation.description'),
                recommendation: t('eligibility.quiz.results.consultation.recommendation'),
                additionalInfo: [
                    t('eligibility.quiz.results.consultation.info.0'),
                    t('eligibility.quiz.results.consultation.info.1'),
                    t('eligibility.quiz.results.consultation.info.2')
                ]
            };
        }
    };

    const handleAnswer = (value: string) => {
        setAnswers(prev => ({ ...prev, [currentQuestion]: value }));
    };

    const nextQuestion = () => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(prev => prev + 1);
        } else {
            calculateResult();
        }
    };

    const prevQuestion = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(prev => prev - 1);
        }
    };

    const calculateResult = () => {
        const totalScore = questions.reduce((sum, question) => {
            const answer = answers[question.id - 1];
            const option = question.options.find(opt => opt.value === answer);
            return sum + (option?.score || 0);
        }, 0);

        setResult(getResult(totalScore));
        setShowResult(true);
    };

    const handleConsultationClick = () => {
        setShowBooking(true);
    };

    const handleBackToResults = () => {
        setShowBooking(false);
    };

    const resetAll = () => {
        setCurrentQuestion(0);
        setAnswers({});
        setShowResult(false);
        setResult(null);
        setShowQuiz(false);
        setShowBooking(false);
    };

    const getResultColor = (title: string) => {
        switch (title) {
            case t('eligibility.quiz.results.excellent.title'): return "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400";
            case t('eligibility.quiz.results.good.title'): return "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400";
            case t('eligibility.quiz.results.possible.title'): return "border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400";
            default: return "border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400";
        }
    };

    const getResultIcon = (title: string) => {
        switch (title) {
            case t('eligibility.quiz.results.excellent.title'): return <CheckCircle className="w-12 h-12 text-green-600" />;
            case t('eligibility.quiz.results.good.title'): return <CheckCircle className="w-12 h-12 text-blue-600" />;
            case t('eligibility.quiz.results.possible.title'): return <AlertTriangle className="w-12 h-12 text-yellow-600" />;
            default: return <Info className="w-12 h-12 text-orange-600" />;
        }
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(formRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: "top 70%",
                        toggleActions: "play none none reverse"
                    }
                }
            );
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="eligibility"
            ref={sectionRef}
            className="py-20 bg-gradient-to-br from-slate-50 via-slate-200 to-slate-50 dark:from-navy-950 dark:via-navy dark:to-navy-950"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className={cn(
                        "text-4xl md:text-5xl font-bold text-navy dark:text-white mb-6 font-serif",
                        isRTL && "font-arabic"
                    )}>
                        {localizeText(t('eligibility.title_part1'))}{' '}
                        <span className="text-gold">{localizeText(t('eligibility.title_part2'))}</span>
                    </h2>
                    <p className={cn(
                        "text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed",
                        isRTL && "font-arabic"
                    )}>
                        {localizeText(t('eligibility.description'))}
                    </p>
                </div>

                <div ref={formRef} className="max-w-2xl mx-auto">
                    <AnimatePresence mode="wait">
                        {showBooking ? (
                            <motion.div
                                key="booking"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-slate-200 dark:border-slate-700"
                            >
                                {/* Booking Form Header */}
                                <div className="text-center mb-8">
                                    <div className="flex items-center justify-center mb-6">
                                        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center">
                                            <Calendar className="w-8 h-8 text-gold" />
                                        </div>
                                    </div>
                                    <h3 className={cn(
                                        "text-2xl font-bold text-navy dark:text-white mb-4",
                                        isRTL && "font-arabic"
                                    )}>
                                        {localizeText(t('eligibility.booking.title'))}
                                    </h3>
                                    <p className={cn(
                                        "text-lg text-slate-600 dark:text-slate-300 mb-6",
                                        isRTL && "font-arabic"
                                    )}>
                                        {localizeText(t('eligibility.booking.description'))}
                                    </p>
                                </div>

                                {/* Booking Form */}
                                <div className="mb-6">
                                    <BookingForm treatmentType="Dental Implants" />
                                </div>

                                {/* Back Button */}
                                <div className="text-center border-t border-slate-200 dark:border-slate-700 pt-6">
                                    <Button
                                        onClick={handleBackToResults}
                                        variant="outline"
                                        className={cn(
                                            "border-gold text-gold hover:bg-gold hover:text-navy inline-flex items-center",
                                            isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                        )}
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        <span className={cn(isRTL && "font-arabic")}>
                                            {t('eligibility.booking.back_button')}
                                        </span>
                                    </Button>
                                </div>
                            </motion.div>
                        ) : !showQuiz ? (
                            <motion.div
                                key="overview"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-slate-200 dark:border-slate-700"
                            >
                                <h3 className={cn(
                                    "text-2xl font-semibold text-navy dark:text-white mb-8 text-center",
                                    isRTL && "font-arabic"
                                )}>
                                    {localizeText(t('eligibility.general_requirements.title'))}
                                </h3>

                                {/* Eligibility factors */}
                                <div className="space-y-6 mb-8">
                                    {eligibilityFactors.map((factor, index) => (
                                        <div
                                            key={index}
                                            className="p-6 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-gold/50 transition-all duration-300"
                                        >
                                            <div className={cn(
                                                "flex items-start",
                                                isRTL ? "space-x-reverse space-x-4" : "space-x-4"
                                            )}>
                                                <div className="shrink-0 p-2 rounded-lg bg-gold/10">
                                                    {factor.icon}
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className={cn(
                                                        "font-semibold text-navy dark:text-white mb-2",
                                                        isRTL && "font-arabic"
                                                    )}>
                                                        {localizeText(factor.label)}
                                                    </h4>
                                                    <p className={cn(
                                                        "text-slate-600 dark:text-slate-300 text-sm",
                                                        isRTL && "font-arabic"
                                                    )}>
                                                        {localizeText(factor.description)}
                                                    </p>
                                                </div>
                                                <CheckCircle className="w-6 h-6 text-gold shrink-0" />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="text-center">
                                    <p className={cn(
                                        "text-slate-600 dark:text-slate-300 mb-6",
                                        isRTL && "font-arabic"
                                    )}>
                                        {localizeText(t('eligibility.quiz.intro_text'))}
                                    </p>
                                    <Button
                                        size="lg"
                                        onClick={() => setShowQuiz(true)}
                                        className={cn(
                                            "bg-gold hover:bg-gold-600 text-navy px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center",
                                            isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                        )}
                                    >
                                        <span className={cn(isRTL && "font-arabic")}>
                                            {t('eligibility.quiz.start_button')}
                                        </span>
                                        <ChevronRight className="w-5 h-5" />
                                    </Button>
                                </div>
                            </motion.div>
                        ) : !showResult ? (
                            <motion.div
                                key="quiz"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-slate-200 dark:border-slate-700"
                            >
                                {/* Progress Bar */}
                                <div className="mb-8">
                                    <div className={cn(
                                        "flex justify-between text-sm text-slate-600 dark:text-slate-300 mb-2",
                                        isRTL && "font-arabic"
                                    )}>
                                        <span>{localizeText(`${t('eligibility.quiz.question_label')} ${currentQuestion + 1} ${t('eligibility.quiz.of_label')} ${questions.length}`)}</span>
                                        <span>{localizeText(`${Math.round(((currentQuestion + 1) / questions.length) * 100)}%`)}</span>
                                    </div>
                                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                                        <div
                                            className={cn(
                                                "bg-gold h-3 rounded-full transition-all duration-500",
                                                isRTL && "bg-gradient-to-l"
                                            )}
                                            style={{ 
                                                width: `${((currentQuestion + 1) / questions.length) * 100}%`,
                                                ...(isRTL ? { marginLeft: 'auto' } : {})
                                            }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Question */}
                                <div className="mb-8">
                                    <h3 className={cn(
                                        "text-2xl font-semibold text-navy dark:text-white mb-6",
                                        isRTL && "font-arabic"
                                    )}>
                                        {localizeText(questions[currentQuestion].question)}
                                    </h3>

                                    <div className="space-y-4">
                                        {questions[currentQuestion].options.map((option, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleAnswer(option.value)}
                                                className={cn(
                                                    "w-full p-4 rounded-xl border-2 transition-all duration-300",
                                                    isRTL ? "text-right" : "text-left",
                                                    answers[currentQuestion] === option.value
                                                        ? 'border-gold bg-gold/10 text-navy dark:text-white'
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-gold/50 hover:bg-gold/5'
                                                )}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className={cn(
                                                        "font-medium",
                                                        isRTL && "font-arabic"
                                                    )}>
                                                        {localizeText(option.label)}
                                                    </span>
                                                    {answers[currentQuestion] === option.value && (
                                                        <CheckCircle className="w-5 h-5 text-gold" />
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Navigation */}
                                <div className="flex justify-between">
                                    <Button
                                        variant="outline"
                                        onClick={prevQuestion}
                                        disabled={currentQuestion === 0}
                                        className={cn(
                                            "border-gold text-gold hover:bg-gold hover:text-navy flex items-center",
                                            isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                        )}
                                    >
                                        {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                                        <span className={cn(isRTL && "font-arabic")}>
                                            {t('eligibility.quiz.previous_button')}
                                        </span>
                                    </Button>

                                    <Button
                                        onClick={nextQuestion}
                                        disabled={!answers[currentQuestion]}
                                        className={cn(
                                            "bg-gold hover:bg-gold-600 text-navy flex items-center",
                                            isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                        )}
                                    >
                                        <span className={cn(isRTL && "font-arabic")}>
                                            {currentQuestion === questions.length - 1 ? t('eligibility.quiz.get_results_button') : t('eligibility.quiz.next_button')}
                                        </span>
                                        {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </Button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="result"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-slate-200 dark:border-slate-700"
                            >
                                {/* Result */}
                                <div className="text-center mb-8">
                                    <div className="flex justify-center mb-6">
                                        {result && getResultIcon(result.title)}
                                    </div>

                                    <div className={cn(
                                        `inline-flex items-center px-6 py-3 rounded-full text-lg font-semibold mb-6 border-2 ${result && getResultColor(result.title)}`,
                                        isRTL && "font-arabic"
                                    )}>
                                        {result?.title && localizeText(result.title)}
                                    </div>

                                    <h3 className={cn(
                                        "text-2xl font-bold text-navy dark:text-white mb-4",
                                        isRTL && "font-arabic"
                                    )}>
                                        {localizeText(t('eligibility.quiz.results.title'))}
                                    </h3>

                                    <p className={cn(
                                        "text-lg text-slate-600 dark:text-slate-300 mb-6",
                                        isRTL && "font-arabic"
                                    )}>
                                        {result?.description && localizeText(result.description)}
                                    </p>

                                    <div className="bg-gold/10 rounded-xl p-6 mb-6 border border-gold/20">
                                        <h4 className={cn(
                                            "font-semibold text-navy dark:text-white mb-2",
                                            isRTL && "font-arabic"
                                        )}>
                                            {localizeText(t('eligibility.quiz.results.recommendation_label'))}
                                        </h4>
                                        <p className={cn(
                                            "text-slate-600 dark:text-slate-300",
                                            isRTL && "font-arabic"
                                        )}>
                                            {result?.recommendation && localizeText(result.recommendation)}
                                        </p>
                                    </div>

                                    {result?.additionalInfo && (
                                        <div className={cn(isRTL ? "text-right" : "text-left")}>
                                            <h4 className={cn(
                                                "font-semibold text-navy dark:text-white mb-4",
                                                isRTL && "font-arabic"
                                            )}>
                                                {localizeText(t('eligibility.quiz.results.additional_info_label'))}
                                            </h4>
                                            <div className="space-y-3">
                                                {result.additionalInfo.map((info, index) => (
                                                    <div key={index} className={cn(
                                                        "flex items-start",
                                                        isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                                                    )}>
                                                        <CheckCircle className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                                                        <span className={cn(
                                                            "text-slate-600 dark:text-slate-300",
                                                            isRTL && "font-arabic"
                                                        )}>
                                                            {localizeText(info)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Button
                                        onClick={resetAll}
                                        variant="outline"
                                        className={cn(
                                            "flex-1 border-gold text-gold hover:bg-gold hover:text-navy",
                                            isRTL && "font-arabic"
                                        )}
                                    >
                                        {t('eligibility.quiz.retake_button')}
                                    </Button>
                                    <Button
                                        onClick={handleConsultationClick}
                                        className={cn(
                                            "flex-1 bg-gold hover:bg-gold-600 text-navy font-semibold",
                                            isRTL && "font-arabic"
                                        )}
                                    >
                                        {t('eligibility.quiz.schedule_consultation_button')}
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
} 
